import React from 'react';
import { Lock, Image as ImageIcon } from 'lucide-react';
import VASSlider from '../../Shared/VASSlider';
import NRSScale from '../../Shared/NRSScale';
import VRSScale from '../../Shared/VRSScale';
import SegmentedControl from '../../Shared/SegmentedControl';
import EmojiFaceScale from '../../Shared/EmojiFaceScale';
import ThermometerScale from '../../Shared/ThermometerScale';
import SteppedSlider from '../../Shared/SteppedSlider';
import BodyMap from '../../Survey/BodyMap/BodyMap';
import { htmlToPlainText, isNumericLike } from './utils';
import { getOptionBoxWidth, getOptionButtonSizingStyle, getOptionsGroupLayout } from '../../../utils/responseOptionAppearance';

const CanvasElement = ({
  element,
  selectedElement,
  setSelectedElement,
  isSelected: isSelectedProp,
  editingElement,
  setEditingElement,
  editingValue,
  setEditingValue,
  dragging,
  resizing,
  showPositionOverlay,
  isElementLocked,
  handleMouseDown,
  handleDoubleClick,
  handleInlineEditSave,
  handleInlineEditCancel,
  handleResizeStart,
  updateSubElementPosition,
  updateSubElementSize,
  updateResponseConfig,
  snapPosition
}) => {
  // A member of the (possibly multi-) selection gets the ring; the anchor
  // (selectedElement) additionally gets resize handles + inline-edit affordances
  // (FECOA-1523). Fall back to anchor-only when the prop isn't supplied.
  const isSelected = isSelectedProp ?? (selectedElement === element.id);
  return (
    <div
      key={element.id}
      data-element={element.id}
      className={`absolute border-2 select-none transition-shadow ${
        isElementLocked(element.id) 
          ? 'cursor-not-allowed' 
          : editingElement === element.id
            ? 'cursor-text'
            : (element.type === 'text' || element.type === 'instruction' || element.type === 'question')
              ? 'cursor-text hover:cursor-move'
              : 'cursor-move'
      } ${
        isSelected
          ? 'border-primary-500 shadow-lg ring-2 ring-primary-200'
          : 'border-transparent hover:border-gray-300'
      } ${dragging === element.id ? 'opacity-75' : ''} ${
        isElementLocked(element.id) ? 'ring-2 ring-amber-200' : ''
      }`}
      style={{
        left: `${element.position.x}px`,
        top: `${element.position.y}px`,
        width: `${element.size.width}px`,
        height: `${element.size.height}px`,
        padding: element.type === 'button' ? '0' : (element.style?.padding ? `${element.style.padding}px` : '0'),
        backgroundColor: element.type === 'button' ? 'transparent' : (element.style?.backgroundColor || 'transparent'),
        borderRadius: element.type === 'button' ? '0' : (element.style?.borderRadius ? `${element.style.borderRadius}px` : '0'),
        borderWidth: element.type === 'button' ? '0' : (element.style?.borderWidth ? `${element.style.borderWidth}px` : '0'),
        borderColor: element.type === 'button' ? 'transparent' : (element.style?.borderColor || 'transparent'),
        borderStyle: element.type === 'button' ? 'none' : (element.style?.borderStyle || 'solid')
      }}
      onMouseDown={(e) => handleMouseDown(e, element.id)}
      onDoubleClick={(e) => handleDoubleClick(e, element.id)}
    >
      {element.type === 'question' && !element.isUngrouped && (
        <div data-question-inner className="h-full overflow-hidden pointer-events-none" style={{
          fontSize: `${element.style?.fontSize || 16}px`,
          fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
          color: element.style?.color || '#1f2937',
          lineHeight: element.style?.lineHeight || 1.5
        }}>
          {editingElement === element.id ? (
            <textarea
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onBlur={() => handleInlineEditSave(element.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleInlineEditSave(element.id);
                } else if (e.key === 'Escape') {
                  handleInlineEditCancel();
                }
              }}
              autoFocus
              className="w-full h-auto min-h-[60px] p-2 border-2 border-primary-500 rounded resize-none focus:outline-none bg-white pointer-events-auto"
              style={{
                fontSize: `${element.style?.fontSize || 16}px`,
                fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
                fontWeight: element.style?.fontWeight || '400',
                color: element.style?.color || '#1f2937',
                textAlign: element.style?.textAlign || 'left',
                lineHeight: element.style?.lineHeight || 1.5
              }}
            />
          ) : (
            <p
              data-question-text
              style={{
                // textAlign is applied directly on the <p> so it affects only
                // the inline text inside this block. Applying it on the inner
                // wrapper instead caused layout drift when the <p> inherited
                // text-align together with inline-flex siblings below — the
                // <p>'s rendered position shifted outside the element's
                // bounds for center/right. Keeping it local keeps alignment
                // purely a text-inside-<p> concern.
                display: 'block',
                width: '100%',
                fontWeight: element.style?.fontWeight || '400',
                textAlign: element.style?.textAlign || 'left',
                marginBottom: '12px',
                cursor: 'text',
                overflowWrap: 'break-word',
              }}
              dangerouslySetInnerHTML={{ __html: element.data.text }}
            />
          )}
          {/* Free Text Input Area */}
          {element.responseConfig?.layout === 'text' && element.data?.type !== 'numeric' && (
            <div data-scale-content className="mt-2">
              <textarea
                placeholder="Enter your response..."
                disabled
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none bg-gray-50 text-gray-400 cursor-not-allowed"
                style={{ fontSize: `${element.responseConfig?.optionFontSize || 14}px`, minHeight: '60px' }}
              />
              <div className="text-right text-xs text-gray-400 mt-1">
                0 / {element.responseConfig?.maxLength || 500}
              </div>
            </div>
          )}
          {element.data?.type === 'body_map' && (() => {
            const rc = element.responseConfig || {};
            const bodyEditMode = !!rc.bodyMapEditMode;
            const overrides = rc.regionOverrides || {};
            const activeSlug = rc.bodyMapActiveSlug || null;
            // Pass the saved position as-is (null when unset). BodyMap supplies
            // the default placement so the builder and preview stay in sync.
            const panelPosition = rc.selectedPanelPosition || null;
            return (
              <div
                data-scale-content
                data-bodymap-edit={bodyEditMode ? 'true' : 'false'}
                className={`mt-2 ${bodyEditMode ? 'pointer-events-auto' : ''}`}
                onPointerDown={bodyEditMode ? (e) => e.stopPropagation() : undefined}
                onMouseDown={bodyEditMode ? (e) => e.stopPropagation() : undefined}
              >
                <BodyMap
                  value={[]}
                  disabled={!bodyEditMode}
                  editMode={bodyEditMode}
                  overrides={overrides}
                  activeSlug={activeSlug}
                  onActiveSlugChange={(slug) =>
                    updateResponseConfig?.(element.id, { bodyMapActiveSlug: slug })
                  }
                  onOverrideChange={(slug, override) =>
                    updateResponseConfig?.(element.id, {
                      regionOverrides: { ...overrides, [slug]: override },
                    })
                  }
                  panelPosition={panelPosition}
                  onPanelPositionChange={(pos) =>
                    updateResponseConfig?.(element.id, { selectedPanelPosition: pos })
                  }
                  containerWidth={element.size.width}
                />
              </div>
            );
          })()}
          {((element.data.type === 'multiple_choice' && element.data.options) || element.responseOptions || isNumericLike(element.data?.type)) && (
            <div
              data-options-area
              style={{
                textAlign: element.responseConfig?.optionsAlignment ?? 'left'
              }}
            >
              {/* eCOA VAS Slider */}
              {element.responseConfig?.layout === 'vas' && (
                <div data-scale-content className="mt-4">
                  <VASSlider
                    leftAnchor={element.responseConfig?.leftAnchor || "Left anchor"}
                    rightAnchor={element.responseConfig?.rightAnchor || "Right anchor"}
                    value={50}
                    disabled={true}
                    showValue={false}
                    showCurrentValue={element.responseConfig?.showCurrentValue || false}
                    colorGradient={element.responseConfig?.colorGradient || false}
                    hideMetadata={true}
                  />
                </div>
              )}
              
              {/* eCOA NRS Scale — explicit 'nrs' OR numeric-type fallback when
                  no other scale layout is picked. Without the second guard,
                  switching a numeric question to e.g. 'thermometer' would
                  paint both NRS and Thermometer on the same element. */}
              {(element.responseConfig?.layout === 'nrs' || (isNumericLike(element.data?.type) && !['vas', 'thermometer', 'vrs-unipolar', 'vrs-bipolar', 'segmented', 'emoji', 'stepped-slider', 'text'].includes(element.responseConfig?.layout))) && (
                <div data-scale-content className="mt-4">
                  <NRSScale
                    minValue={element.responseConfig?.minValue || 0}
                    maxValue={element.responseConfig?.maxValue || 10}
                    leftAnchor={element.responseConfig?.leftAnchor || "Left anchor"}
                    rightAnchor={element.responseConfig?.rightAnchor || "Right anchor"}
                    anchorPlacement={element.responseConfig?.anchorPlacement || "below"}
                    leftAnchorOffset={{ x: element.responseConfig?.leftAnchorX || 0, y: element.responseConfig?.leftAnchorY || 0 }}
                    rightAnchorOffset={{ x: element.responseConfig?.rightAnchorX || 0, y: element.responseConfig?.rightAnchorY || 0 }}
                    value={null}
                    disabled={true}
                    orientation={element.responseConfig?.orientation || 'horizontal'}
                    showIndicators={element.responseConfig?.showIndicators !== false}
                    colorGradient={element.responseConfig?.colorGradient || false}
                    hideMetadata={true}
                  />
                </div>
              )}
              
              {/* eCOA VRS Scale */}
              {(element.responseConfig?.layout === 'vrs-unipolar' || element.responseConfig?.layout === 'vrs-bipolar') && (() => {
                const optAlign = element.responseConfig?.optionsAlignment ?? 'left';
                return (
                <div data-scale-content className={`mt-4 flex ${
                  optAlign === 'center' ? 'justify-center' :
                  optAlign === 'right' ? 'justify-end' :
                  'justify-start'
                }`}>
                  <VRSScale
                    options={(Array.isArray(element.responseOptions) ? element.responseOptions : Array.isArray(element.data?.options) ? element.data.options : []).map((opt, idx) => ({
                      value: typeof opt === 'object' ? opt.value : idx,
                      label: typeof opt === 'string' ? opt : (opt.label || opt)
                    }))}
                    value={null}
                    disabled={true}
                    orientation={element.responseConfig?.orientation || 'vertical'}
                    type={element.responseConfig?.layout === 'vrs-bipolar' ? 'bipolar' : 'unipolar'}
                    spacing={element.responseConfig?.spacing || 12}
                    buttonHeight={element.responseConfig?.vrsButtonHeight}
                    buttonMinWidth={element.responseConfig?.vrsButtonMinWidth}
                    fontSize={element.responseConfig?.vrsFontSize}
                    fontWeight={element.responseConfig?.vrsFontWeight}
                    borderRadius={element.responseConfig?.vrsBorderRadius}
                    borderWidth={element.responseConfig?.vrsBorderWidth}
                    selectedColor={element.responseConfig?.vrsSelectedColor}
                    unselectedColor={element.responseConfig?.vrsUnselectedColor}
                    selectedTextColor={element.responseConfig?.vrsSelectedTextColor}
                    unselectedTextColor={element.responseConfig?.vrsUnselectedTextColor}
                    borderColor={element.responseConfig?.vrsBorderColor}
                    selectedBorderColor={element.responseConfig?.vrsSelectedBorderColor}
                    hoverColor={element.responseConfig?.vrsHoverColor}
                    hoverBorderColor={element.responseConfig?.vrsHoverBorderColor}
                  />
                </div>
                );
              })()}

              {/* Segmented Control */}
              {element.responseConfig?.layout === 'segmented' && (
                <div data-scale-content className="mt-4">
                  <SegmentedControl
                    options={element.responseOptions || element.data?.options || []}
                    disabled={true}
                    fontSize={element.responseConfig?.optionFontSize || 14}
                    height={element.responseConfig?.segmentHeight || 44}
                    activeColor={element.responseConfig?.activeColor || '#0d9488'}
                  />
                </div>
              )}

              {/* Emoji/Face Scale */}
              {element.responseConfig?.layout === 'emoji' && (
                <div data-scale-content className="mt-4">
                  <EmojiFaceScale
                    options={element.responseOptions || element.data?.options || []}
                    disabled={true}
                    faceSize={element.responseConfig?.faceSize || 48}
                    fontSize={element.responseConfig?.optionFontSize || 12}
                    showLabels={element.responseConfig?.showLabels !== false}
                    showValues={element.responseConfig?.showValues || false}
                  />
                </div>
              )}

              {/* Thermometer Scale */}
              {element.responseConfig?.layout === 'thermometer' && (
                <div data-scale-content className="mt-4 flex justify-center">
                  <ThermometerScale
                    minValue={element.responseConfig?.minValue ?? 0}
                    maxValue={element.responseConfig?.maxValue ?? 100}
                    step={element.responseConfig?.step ?? 10}
                    disabled={true}
                    topAnchor={element.responseConfig?.topAnchor || element.responseConfig?.rightAnchor || 'Best'}
                    bottomAnchor={element.responseConfig?.bottomAnchor || element.responseConfig?.leftAnchor || 'Worst'}
                    height={Math.min(element.size?.height - 60, 400) || 300}
                    fillColor={element.responseConfig?.fillColor || '#0d9488'}
                    showCurrentValue={element.responseConfig?.showCurrentValue !== false}
                  />
                </div>
              )}

              {/* Stepped Slider */}
              {element.responseConfig?.layout === 'stepped-slider' && (
                <div data-scale-content className="mt-4">
                  <SteppedSlider
                    options={element.responseOptions || element.data?.options || []}
                    disabled={true}
                    activeColor={element.responseConfig?.activeColor || '#0d9488'}
                    showLabels={element.responseConfig?.showLabels !== false}
                    showValues={element.responseConfig?.showValues || false}
                    fontSize={element.responseConfig?.optionFontSize || 12}
                  />
                </div>
              )}

              {/* Standard response options (vertical, horizontal, grid) */}
              {!['vas', 'nrs', 'vrs-unipolar', 'vrs-bipolar', 'segmented', 'emoji', 'thermometer', 'stepped-slider', 'text'].includes(element.responseConfig?.layout) && (() => {
                // Layout/sizing comes from the shared helper so the canvas renders
                // IDENTICALLY to the survey preview/participant. `boolean` (yes/no)
                // resolves to vertical inside the helper, so yes/no aligns and flows
                // exactly like ordinal/nominal. The parent `data-options-area`
                // applies textAlign (optionsAlignment) which positions the inline,
                // content-hugging vertical group; horizontal/grid use justifyContent.
                const {
                  className: optionsListClassName,
                  style: optionsListStyle,
                  buttonFillsWidth,
                } = getOptionsGroupLayout(element.responseConfig || {});

                // FECOA-1643: a yes/no element may hold its raw config dict
                // ({ type:'boolean', yesLabel, noLabel }) instead of an options
                // array. The canvas needs an array to map over — an unnormalized
                // dict renders NOTHING (the reported "buttons missing on canvas"
                // symptom). Normalize it here so the canvas shows the item's real
                // labels, matching the preview/participant renderer.
                const rawOptions = element.data?.options ?? element.responseOptions;
                const elementType = (element.data?.type || element.data?.question_type || '').toLowerCase();
                const isBooleanEl = elementType === 'boolean' || elementType === 'yes_no'
                  || elementType === 'yesno' || element.responseConfig?.layout === 'boolean';
                const boolDict = isBooleanEl && rawOptions && typeof rawOptions === 'object'
                  && !Array.isArray(rawOptions) ? rawOptions : null;
                const optionItems = boolDict
                  ? [
                      { order: 1, label: boolDict.yesLabel || 'Yes', value: 1 },
                      { order: 2, label: boolDict.noLabel || 'No', value: 0 },
                    ]
                  : Array.isArray(element.data?.options)
                    ? element.data.options
                    : Array.isArray(element.responseOptions)
                      ? element.responseOptions
                      : [];

                return (
                <div
                  data-options-list
                  className={optionsListClassName}
                  style={optionsListStyle}
                >
                  {optionItems.map((option, idx) => {
                    const buttonStyle = element.responseConfig?.buttonStyle;
                    const display = element.responseConfig?.display;
                    // Show as button if buttonStyle is set OR if display mode includes 'buttons'
                    const showAsButton = (buttonStyle && ['outline', 'filled', 'solid'].includes(buttonStyle)) || 
                                        (display && display.includes('button'));
                    
                    if (showAsButton) {
                      // Render as button with customizable colors
                      const rc = element.responseConfig || {};
                      const isFilled = rc.buttonStyle === 'filled' || rc.buttonStyle === 'solid';
                      const btnBgColor = rc.btnBgColor || (isFilled ? '#0d9488' : '#ffffff');
                      const btnTextColor = rc.btnTextColor || (isFilled ? '#ffffff' : '#0d9488');
                      const btnBorderColor = rc.btnBorderColor || '#0d9488';
                      const btnBorderRadius = rc.btnBorderRadius ?? 4;
                      const btnBorderWidth = rc.btnBorderWidth ?? 1;
                      const btnFontWeight = rc.btnFontWeight || '400';
                      const optionFontSize = rc.optionFontSize || 14;
                      const optionPadding = rc.optionPadding || 8;

                      return (
                        <button
                          key={idx}
                          data-option-index={idx}
                          disabled
                          tabIndex={-1}
                          className={`
                            transition-colors pointer-events-none
                            ${buttonFillsWidth ? 'w-full' : 'inline-block'}
                          `}
                          style={{
                            fontSize: `${optionFontSize}px`,
                            fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
                            padding: `${optionPadding}px ${optionPadding * 2}px`,
                            minWidth: buttonFillsWidth ? '0' : 'auto',
                            whiteSpace: 'nowrap',
                            ...getOptionButtonSizingStyle(rc),
                            borderRadius: `${btnBorderRadius}px`,
                            borderWidth: `${btnBorderWidth}px`,
                            borderStyle: 'solid',
                            fontWeight: btnFontWeight,
                            backgroundColor: btnBgColor,
                            color: btnTextColor,
                            borderColor: btnBorderColor,
                          }}
                        >
                          <span dangerouslySetInnerHTML={{ __html: option.label || option }} />
                        </button>
                      );
                    } else {
                      // Render as radio/checkbox
                      return (
                        <div
                          key={idx}
                          data-option-index={idx}
                          className="flex items-center pointer-events-none"
                          style={{
                            padding: `${element.responseConfig?.optionPadding || 4}px`,
                            fontSize: `${element.responseConfig?.optionFontSize || 14}px`
                          }}
                        >
                          <input 
                            type={element.multiSelect ? "checkbox" : "radio"}
                            disabled 
                            style={{
                              width: `${element.responseConfig?.radioSize || 16}px`,
                              height: `${element.responseConfig?.radioSize || 16}px`,
                              marginRight: '8px'
                            }}
                          />
                          <span dangerouslySetInnerHTML={{ __html: option.label || option }} />
                        </div>
                      );
                    }
                  })}
                </div>
                );
              })()}
            </div>
          )}
        </div>
      )}
      {element.type === 'question' && element.isUngrouped && (() => {
        const equalWidth = element.responseConfig?.equalWidth;
        const optionBoxWidth = getOptionBoxWidth(element.responseConfig);
        const optionSubs = element.subElements?.filter(s => s.type === 'question-option') || [];
        const SCALE_SUB_TYPES = ['question-vas', 'question-nrs', 'question-vrs', 'question-segmented', 'question-emoji', 'question-thermometer', 'question-stepped-slider', 'question-text-input'];
        // Radio/checkbox options need wrapper padding to match grouped render — measured
        // rects include the grouped option's outer padding, but the inner <label> in
        // ungrouped mode has none, so without restoring the wrapper padding the radio
        // shifts inward by optionPadding on toggle. Buttons own their own padding.
        const ungroupedButtonStyle = element.responseConfig?.buttonStyle;
        const optionRendersAsButton = ungroupedButtonStyle && ['outline', 'filled', 'solid'].includes(ungroupedButtonStyle);
        const radioOptionPadding = element.responseConfig?.optionPadding ?? 4;
        let equalOptionWidth = null;
        if (equalWidth && optionSubs.length > 0) {
          const optFontSize = element.responseConfig?.optionFontSize || 14;
          const optPadding = element.responseConfig?.optionPadding || 8;
          const charWidth = optFontSize * 0.6;
          const maxTextLen = Math.max(...optionSubs.map(s => htmlToPlainText(s.content).length));
          equalOptionWidth = Math.ceil(maxTextLen * charWidth + optPadding * 4 + 2);
        }
        return (
        <div
          className="h-full relative"
          style={{
            fontSize: `${element.style?.fontSize || 16}px`,
            fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
            color: element.style?.color || '#1f2937',
            textAlign: element.style?.textAlign || 'left',
            lineHeight: element.style?.lineHeight || 1.5
          }}
        >
          {element.subElements?.map((sub) => (
            <div
              key={sub.id}
              className="absolute group cursor-move transition-colors rounded outline outline-1 outline-transparent hover:outline-primary-300"
              style={{
                left: `${sub.position.x}px`,
                top: `${sub.position.y}px`,
                // Anchors size to their text: a measured width would clip the
                // label as soon as the author edits it to something longer.
                width: sub.type === 'question-nrs-anchor'
                  ? 'auto'
                  : sub.type === 'question-option' && optionBoxWidth
                  ? optionBoxWidth
                  : sub.measured || sub.type === 'question-text' || SCALE_SUB_TYPES.includes(sub.type)
                  ? `${sub.size.width}px`
                  : (equalOptionWidth ? `${equalOptionWidth}px` : 'auto'),
                padding: sub.measured
                  ? (sub.type === 'question-option' && !optionRendersAsButton ? `${radioOptionPadding}px` : '0')
                  : (sub.type === 'question-text' ? '4px 8px' : '4px 12px')
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                setSelectedElement(element.id);

                const startX = e.clientX;
                const startY = e.clientY;
                const startPos = { ...sub.position };
                const parentW = element.size.width - (element.style?.padding || 0) * 2;
                const parentH = element.size.height - (element.style?.padding || 0) * 2;

                const handleMove = (moveE) => {
                  const dx = moveE.clientX - startX;
                  const dy = moveE.clientY - startY;
                  const newX = snapPosition(Math.max(0, Math.min(startPos.x + dx, parentW - 40)));
                  const newY = snapPosition(Math.max(0, Math.min(startPos.y + dy, parentH - 20)));
                  updateSubElementPosition(element.id, sub.id, { x: newX, y: newY });
                };

                const handleUp = () => {
                  window.removeEventListener('mousemove', handleMove);
                  window.removeEventListener('mouseup', handleUp);
                };

                window.addEventListener('mousemove', handleMove);
                window.addEventListener('mouseup', handleUp);
              }}
            >
              {sub.type === 'question-text' ? (
                <div style={{
                  fontSize: `${element.style?.fontSize || 16}px`,
                  fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
                  fontWeight: element.style?.fontWeight || '400',
                  color: element.style?.color || '#1f2937',
                  lineHeight: element.style?.lineHeight || 1.5,
                  textAlign: element.style?.textAlign || 'left'
                }}>
                  <span dangerouslySetInnerHTML={{ __html: sub.content }} />
                </div>
              ) : sub.type === 'question-vas' ? (
                <div style={{ width: `${sub.size.width}px` }}>
                  <VASSlider
                    leftAnchor={element.responseConfig?.leftAnchor || "Left anchor"}
                    rightAnchor={element.responseConfig?.rightAnchor || "Right anchor"}
                    value={50}
                    disabled={true}
                    showValue={false}
                    showCurrentValue={element.responseConfig?.showCurrentValue || false}
                    colorGradient={element.responseConfig?.colorGradient || false}
                    hideMetadata={true}
                  />
                </div>
              ) : sub.type === 'question-nrs' ? (
                <div style={{ width: `${sub.size.width}px` }}>
                  <NRSScale
                    minValue={element.responseConfig?.minValue || 0}
                    maxValue={element.responseConfig?.maxValue || 10}
                    leftAnchor={element.responseConfig?.leftAnchor || "Left anchor"}
                    rightAnchor={element.responseConfig?.rightAnchor || "Right anchor"}
                    anchorPlacement={element.responseConfig?.anchorPlacement || "below"}
                    leftAnchorOffset={{ x: element.responseConfig?.leftAnchorX || 0, y: element.responseConfig?.leftAnchorY || 0 }}
                    rightAnchorOffset={{ x: element.responseConfig?.rightAnchorX || 0, y: element.responseConfig?.rightAnchorY || 0 }}
                    hideAnchors={element.subElements?.some(s => s.type === 'question-nrs-anchor')}
                    value={null}
                    disabled={true}
                    orientation={element.responseConfig?.orientation || 'horizontal'}
                    showIndicators={element.responseConfig?.showIndicators !== false}
                    colorGradient={element.responseConfig?.colorGradient || false}
                    hideMetadata={true}
                  />
                </div>
              ) : sub.type === 'question-nrs-anchor' ? (
                <div
                  className="text-sm font-medium text-gray-700 leading-tight whitespace-nowrap"
                  style={{
                    textAlign: sub.anchorSide === 'right' ? 'right' : 'left',
                    fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
                  }}
                >
                  {/* Live value, not the copy taken at ungroup, so editing the
                      anchor in Properties still updates the placed label. */}
                  {String(
                    (sub.anchorSide === 'right'
                      ? element.responseConfig?.rightAnchor
                      : element.responseConfig?.leftAnchor) ?? sub.content ?? '',
                  ).split(/<br\s*\/?>/i).map((part, i, arr) => (
                    <span key={i}>{part.trim()}{i < arr.length - 1 ? <br /> : null}</span>
                  ))}
                </div>
              ) : sub.type === 'question-vrs' ? (
                <div style={{ width: `${sub.size.width}px` }}>
                  <VRSScale
                    options={(Array.isArray(element.responseOptions) ? element.responseOptions : Array.isArray(element.data?.options) ? element.data.options : []).map((opt, idx) => ({
                      value: typeof opt === 'object' ? opt.value : idx,
                      label: typeof opt === 'string' ? opt : (opt.label || opt)
                    }))}
                    value={null}
                    disabled={true}
                    orientation={element.responseConfig?.orientation || 'vertical'}
                    type={element.responseConfig?.layout === 'vrs-bipolar' ? 'bipolar' : 'unipolar'}
                    spacing={element.responseConfig?.spacing || 12}
                    buttonHeight={element.responseConfig?.vrsButtonHeight}
                    buttonMinWidth={element.responseConfig?.vrsButtonMinWidth}
                    fontSize={element.responseConfig?.vrsFontSize}
                    fontWeight={element.responseConfig?.vrsFontWeight}
                    borderRadius={element.responseConfig?.vrsBorderRadius}
                    borderWidth={element.responseConfig?.vrsBorderWidth}
                    selectedColor={element.responseConfig?.vrsSelectedColor}
                    unselectedColor={element.responseConfig?.vrsUnselectedColor}
                    selectedTextColor={element.responseConfig?.vrsSelectedTextColor}
                    unselectedTextColor={element.responseConfig?.vrsUnselectedTextColor}
                    borderColor={element.responseConfig?.vrsBorderColor}
                    selectedBorderColor={element.responseConfig?.vrsSelectedBorderColor}
                    hoverColor={element.responseConfig?.vrsHoverColor}
                    hoverBorderColor={element.responseConfig?.vrsHoverBorderColor}
                  />
                </div>
              ) : sub.type === 'question-segmented' ? (
                <div style={{ width: `${sub.size.width}px` }}>
                  <SegmentedControl
                    options={element.responseOptions || element.data?.options || []}
                    disabled={true}
                    fontSize={element.responseConfig?.optionFontSize || 14}
                    height={element.responseConfig?.segmentHeight || 44}
                    activeColor={element.responseConfig?.activeColor || '#0d9488'}
                  />
                </div>
              ) : sub.type === 'question-emoji' ? (
                <div style={{ width: `${sub.size.width}px` }}>
                  <EmojiFaceScale
                    options={element.responseOptions || element.data?.options || []}
                    disabled={true}
                    faceSize={element.responseConfig?.faceSize || 48}
                    fontSize={element.responseConfig?.optionFontSize || 12}
                    showLabels={element.responseConfig?.showLabels !== false}
                    showValues={element.responseConfig?.showValues || false}
                  />
                </div>
              ) : sub.type === 'question-thermometer' ? (
                <div style={{ width: `${sub.size.width}px` }} className="flex justify-center">
                  <ThermometerScale
                    minValue={element.responseConfig?.minValue ?? 0}
                    maxValue={element.responseConfig?.maxValue ?? 100}
                    step={element.responseConfig?.step ?? 10}
                    disabled={true}
                    topAnchor={element.responseConfig?.topAnchor || element.responseConfig?.rightAnchor || 'Best'}
                    bottomAnchor={element.responseConfig?.bottomAnchor || element.responseConfig?.leftAnchor || 'Worst'}
                    height={Math.min(sub.size.height - 20, 400) || 300}
                    fillColor={element.responseConfig?.fillColor || '#0d9488'}
                    showCurrentValue={element.responseConfig?.showCurrentValue !== false}
                  />
                </div>
              ) : sub.type === 'question-stepped-slider' ? (
                <div style={{ width: `${sub.size.width}px` }}>
                  <SteppedSlider
                    options={element.responseOptions || element.data?.options || []}
                    disabled={true}
                    activeColor={element.responseConfig?.activeColor || '#0d9488'}
                    showLabels={element.responseConfig?.showLabels !== false}
                    showValues={element.responseConfig?.showValues || false}
                    fontSize={element.responseConfig?.optionFontSize || 12}
                  />
                </div>
              ) : sub.type === 'question-text-input' ? (
                <div style={{ width: `${sub.size.width}px` }}>
                  <textarea
                    placeholder="Enter your response..."
                    disabled
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none bg-gray-50 text-gray-400 cursor-not-allowed"
                    style={{ fontSize: `${element.responseConfig?.optionFontSize || 14}px`, minHeight: '60px' }}
                  />
                  <div className="text-right text-xs text-gray-400 mt-1">
                    0 / {element.responseConfig?.maxLength || 500}
                  </div>
                </div>
              ) : (() => {
                const rc = element.responseConfig || {};
                const buttonStyle = rc.buttonStyle;
                const showAsButton = buttonStyle && ['outline', 'filled', 'solid'].includes(buttonStyle);
                const optFontSize = rc.optionFontSize || sub.style?.fontSize || 14;
                const optPadding = rc.optionPadding || 8;

                if (showAsButton) {
                  const isFilled = buttonStyle === 'filled' || buttonStyle === 'solid';
                  const btnBgColor = rc.btnBgColor || (isFilled ? '#0d9488' : '#ffffff');
                  const btnTextColor = rc.btnTextColor || (isFilled ? '#ffffff' : '#0d9488');
                  const btnBorderColor = rc.btnBorderColor || '#0d9488';
                  const btnBorderRadius = rc.btnBorderRadius ?? 4;
                  const btnBorderWidth = rc.btnBorderWidth ?? 1;
                  const btnFontWeight = rc.btnFontWeight || '400';
                  const equalWidth = rc.equalWidth;
                  return (
                    <button
                      disabled
                      tabIndex={-1}
                      className={`transition-colors pointer-events-none ${equalWidth ? 'w-full' : 'inline-block'}`}
                      style={{
                        fontSize: `${optFontSize}px`,
                        fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
                        padding: `${optPadding}px ${optPadding * 2}px`,
                        minWidth: equalWidth ? '0' : 'auto',
                        whiteSpace: 'nowrap',
                        ...getOptionButtonSizingStyle(rc),
                        borderRadius: `${btnBorderRadius}px`,
                        borderWidth: `${btnBorderWidth}px`,
                        borderStyle: 'solid',
                        fontWeight: btnFontWeight,
                        backgroundColor: btnBgColor,
                        color: btnTextColor,
                        borderColor: btnBorderColor,
                      }}
                    >
                      <span dangerouslySetInnerHTML={{ __html: sub.content }} />
                    </button>
                  );
                }

                return (
                  <label className="flex items-center" style={{
                    fontSize: `${optFontSize}px`,
                    color: sub.style?.color || '#1f2937'
                  }}>
                    <input
                      type="radio"
                      disabled
                      className="mr-2 flex-shrink-0"
                      style={{ width: `${element.responseConfig?.radioSize || 16}px`, height: `${element.responseConfig?.radioSize || 16}px` }}
                    />
                    <span dangerouslySetInnerHTML={{ __html: sub.content }} />
                  </label>
                );
              })()}

              {/* Coordinate tooltip on hover */}
              {selectedElement === element.id && (
                <div className="absolute -top-5 left-0 text-[8px] text-primary-500 font-mono opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                  {Math.round(sub.position.x)}, {Math.round(sub.position.y)}
                </div>
              )}
            </div>
          ))}
        </div>
        );
      })()}
      {(element.type === 'text' || element.type === 'instruction') && (
        <div className="h-full">
          {editingElement === element.id ? (
            <textarea
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onBlur={() => handleInlineEditSave(element.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleInlineEditSave(element.id);
                } else if (e.key === 'Escape') {
                  handleInlineEditCancel();
                }
              }}
              autoFocus
              className="w-full h-full resize-none focus:outline-none"
              style={{
                fontSize: `${element.style?.fontSize || 16}px`,
                fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
                fontWeight: element.style?.fontWeight || '400',
                color: element.style?.color || '#000000',
                textAlign: element.style?.textAlign || 'left',
                lineHeight: element.style?.lineHeight || 1.5,
                padding: '8px 12px',
                background: 'transparent',
                border: '1px solid #79d9c1',
                borderRadius: '2px',
                boxShadow: '0 0 0 2px rgba(36, 166, 139, 0.15)',
                caretColor: '#007764'
              }}
            />
          ) : (
            <p style={{
              fontSize: `${element.style?.fontSize || 16}px`,
              fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
              fontWeight: element.style?.fontWeight || '400',
              color: element.style?.color || '#000000',
              textAlign: element.style?.textAlign || 'left',
              lineHeight: element.style?.lineHeight || 1.5,
              cursor: 'text'
            }}
            dangerouslySetInnerHTML={{ __html: element.content }}
            />
          )}
        </div>
      )}
      {element.type === 'image' && (
        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
          <ImageIcon className="w-12 h-12 text-gray-400" />
        </div>
      )}
      {element.type === 'button' && (
        <button
          className="w-full h-full flex items-center justify-center font-semibold cursor-pointer hover:opacity-90 transition-opacity"
          style={{
            fontSize: `${element.style?.fontSize || 16}px`,
            fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
            fontWeight: element.style?.fontWeight || '600',
            color: element.style?.color || '#ffffff',
            backgroundColor: element.style?.backgroundColor || '#0d9488',
            borderRadius: element.style?.borderRadius ? `${element.style.borderRadius}px` : '8px',
            borderWidth: element.style?.borderWidth ? `${element.style.borderWidth}px` : '0',
            borderColor: element.style?.borderColor || '#0d9488',
            borderStyle: element.style?.borderStyle || 'solid'
          }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            setEditingElement(element.id);
            setEditingValue(element.content ?? '');
          }}
        >
          {editingElement === element.id ? (
            <input
              type="text"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onBlur={() => handleInlineEditSave(element.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleInlineEditSave(element.id);
                } else if (e.key === 'Escape') {
                  handleInlineEditCancel();
                }
              }}
              autoFocus
              className="w-full bg-transparent text-center border-2 border-white rounded px-2 py-1 focus:outline-none"
              style={{
                fontSize: `${element.style?.fontSize || 16}px`,
                fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
                fontWeight: element.style?.fontWeight || '600',
                color: element.style?.color || '#ffffff'
              }}
            />
          ) : (
            element.content || (element.buttonType === 'back' ? 'Back' : element.buttonType === 'submit' ? 'Submit' : 'Next')
          )}
        </button>
      )}

      {/* Divider */}
      {element.type === 'divider' && (
        <div 
          className={element.config?.style === 'dashed' ? 'border-dashed' : element.config?.style === 'dotted' ? 'border-dotted' : ''}
          style={{
            width: '100%',
            height: `${element.config?.thickness || 2}px`,
            backgroundColor: element.style?.backgroundColor || '#d1d5db',
            borderRadius: `${element.style?.borderRadius || 0}px`
          }}
        />
      )}

      {/* Text Input */}
      {element.type === 'text-input' && (
        <div className="h-full flex flex-col justify-center">
          <label className="block text-sm font-medium mb-1" style={{ fontSize: `${element.style?.fontSize || 14}px` }}>
            {element.label || ''}
            {element.config?.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {element.config?.multiline ? (
            <textarea
              placeholder={element.placeholder || 'Enter text...'}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              style={{ fontSize: `${element.style?.fontSize || 14}px` }}
              rows="3"
              disabled
            />
          ) : (
            <input
              type="text"
              placeholder={element.placeholder || 'Enter text...'}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              style={{ fontSize: `${element.style?.fontSize || 14}px` }}
              disabled
            />
          )}
        </div>
      )}

      {/* Number Input */}
      {element.type === 'number-input' && (
        <div className="h-full flex flex-col justify-center">
          <label className="block text-sm font-medium mb-1" style={{ fontSize: `${element.style?.fontSize || 14}px` }}>
            {element.label || 'Number Input Label'}
            {element.config?.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <input
            type="number"
            placeholder={element.placeholder || '0'}
            min={element.config?.min}
            max={element.config?.max}
            step={element.config?.step || 1}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            style={{ fontSize: `${element.style?.fontSize || 14}px` }}
            disabled
          />
        </div>
      )}

      {/* Date Picker */}
      {element.type === 'date-picker' && (
        <div className="h-full flex flex-col justify-center">
          <label className="block text-sm font-medium mb-1" style={{ fontSize: `${element.style?.fontSize || 14}px` }}>
            {element.label || 'Date Picker Label'}
            {element.config?.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <input
            type={element.config?.includeTime ? 'datetime-local' : 'date'}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            style={{ fontSize: `${element.style?.fontSize || 14}px` }}
            disabled
          />
        </div>
      )}

      {/* Checkbox Group */}
      {element.type === 'checkbox' && (
        <div className="h-full overflow-auto">
          <label className="block text-sm font-medium mb-2" style={{ fontSize: `${element.style?.fontSize || 14}px` }}>
            {element.label || 'Select all that apply'}
            {element.config?.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className={element.config?.layout === 'horizontal' ? 'flex flex-wrap gap-4' : 'space-y-2'}>
            {(element.options || []).map((option, idx) => (
              <label key={idx} className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 mr-2"
                  disabled
                />
                <span style={{ fontSize: `${element.style?.fontSize || 14}px` }}>{option}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Scale / Slider */}
      {element.type === 'scale' && (
        <div className="h-full flex flex-col justify-center px-2">
          <label className="block text-sm font-medium mb-2" style={{ fontSize: `${element.style?.fontSize || 14}px` }}>
            {element.label || 'Rate your experience'}
            {element.config?.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className="flex items-center space-x-3">
            {element.config?.showLabels && element.config?.minLabel && (
              <span className="text-xs text-gray-600 min-w-[60px]">{element.config.minLabel}</span>
            )}
            <input
              type="range"
              min={element.config?.min || 0}
              max={element.config?.max || 10}
              step={element.config?.step || 1}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
              disabled
            />
            {element.config?.showLabels && element.config?.maxLabel && (
              <span className="text-xs text-gray-600 min-w-[60px] text-right">{element.config.maxLabel}</span>
            )}
          </div>
          {element.config?.showValue && (
            <div className="text-center text-sm font-semibold text-primary-600 mt-2">
              {Math.floor((element.config?.min || 0) + (element.config?.max || 10) / 2)}
            </div>
          )}
        </div>
      )}
      
      {/* Lock Indicator Badge */}
      {isElementLocked(element.id) && (
        <div className="absolute -top-2 -right-2 bg-amber-500 text-white p-1 rounded-full shadow-md pointer-events-none">
          <Lock className="w-3 h-3" />
        </div>
      )}
      
      {/* Position Overlay - Shows X/Y when dragging */}
      {showPositionOverlay && dragging === element.id && (
        <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-3 py-1.5 rounded-lg shadow-lg pointer-events-none z-50 whitespace-nowrap">
          <div className="flex items-center space-x-3 text-xs font-mono">
            <span className="font-semibold">X: {Math.round(element.position.x)}</span>
            <span className="text-gray-400">•</span>
            <span className="font-semibold">Y: {Math.round(element.position.y)}</span>
          </div>
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-900" />
        </div>
      )}
      
      {/* Size Overlay - Shows W x H when resizing */}
      {showPositionOverlay && resizing && resizing.elementId === element.id && (
        <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 bg-primary-600 text-white px-3 py-1.5 rounded-lg shadow-lg pointer-events-none z-50 whitespace-nowrap">
          <div className="flex items-center space-x-3 text-xs font-mono">
            <span className="font-semibold">W: {Math.round(element.size.width)}</span>
            <span className="text-primary-100">×</span>
            <span className="font-semibold">H: {Math.round(element.size.height)}</span>
          </div>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-b-4 border-b-primary-600" />
        </div>
      )}
      
      {/* PowerPoint-Style Resize Handles */}
      {selectedElement === element.id && !isElementLocked(element.id) && (
        <>
          {/* Corner Handles */}
          <div 
            className="absolute -top-1 -left-1 w-3 h-3 bg-white border-2 border-primary-500 rounded-sm cursor-nw-resize hover:bg-primary-50"
            onMouseDown={(e) => {
              e.stopPropagation();
              handleResizeStart(e, element.id, 'nw');
            }}
            title="Resize from top-left"
          />
          <div 
            className="absolute -top-1 -right-1 w-3 h-3 bg-white border-2 border-primary-500 rounded-sm cursor-ne-resize hover:bg-primary-50"
            onMouseDown={(e) => {
              e.stopPropagation();
              handleResizeStart(e, element.id, 'ne');
            }}
            title="Resize from top-right"
          />
          <div 
            className="absolute -bottom-1 -left-1 w-3 h-3 bg-white border-2 border-primary-500 rounded-sm cursor-sw-resize hover:bg-primary-50"
            onMouseDown={(e) => {
              e.stopPropagation();
              handleResizeStart(e, element.id, 'sw');
            }}
            title="Resize from bottom-left"
          />
          <div 
            className="absolute -bottom-1 -right-1 w-3 h-3 bg-white border-2 border-primary-500 rounded-sm cursor-se-resize hover:bg-primary-50"
            onMouseDown={(e) => {
              e.stopPropagation();
              handleResizeStart(e, element.id, 'se');
            }}
            title="Resize from bottom-right"
          />
          
          {/* Edge Handles */}
          <div 
            className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-2 border-primary-500 rounded-sm cursor-n-resize hover:bg-primary-50"
            onMouseDown={(e) => {
              e.stopPropagation();
              handleResizeStart(e, element.id, 'n');
            }}
            title="Resize from top"
          />
          <div 
            className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-2 border-primary-500 rounded-sm cursor-s-resize hover:bg-primary-50"
            onMouseDown={(e) => {
              e.stopPropagation();
              handleResizeStart(e, element.id, 's');
            }}
            title="Resize from bottom"
          />
          <div 
            className="absolute -left-1 top-1/2 -translate-y-1/2 w-3 h-3 bg-white border-2 border-primary-500 rounded-sm cursor-w-resize hover:bg-primary-50"
            onMouseDown={(e) => {
              e.stopPropagation();
              handleResizeStart(e, element.id, 'w');
            }}
            title="Resize from left"
          />
          <div 
            className="absolute -right-1 top-1/2 -translate-y-1/2 w-3 h-3 bg-white border-2 border-primary-500 rounded-sm cursor-e-resize hover:bg-primary-50"
            onMouseDown={(e) => {
              e.stopPropagation();
              handleResizeStart(e, element.id, 'e');
            }}
            title="Resize from right"
          />
        </>
      )}
    </div>
  );
};

export default CanvasElement;
